import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotebook } from '@/contexts/NotebookContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Play,
  FastForward,
  RotateCcw,
  StopCircle,
  Plus,
  ChevronLeft,
  Save,
  MoreHorizontal,
  Circle,
  CheckCircle,
  XCircle,
  Loader2,
  Pencil,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NotebookToolbarProps {
  onToggleHelp?: () => void;
  showHelp?: boolean;
}

export default function NotebookToolbar({ onToggleHelp, showHelp }: NotebookToolbarProps) {
  const navigate = useNavigate();
  const {
    state: { notebook, sessionStatus, selectedCellId, executingCellId, dirtySourceMap },
    addCell,
    startKernel,
    stopKernel,
    restartKernel,
    runCell,
    runAllCells,
    saveCellSource,
    renameNotebook,
  } = useNotebook();

  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  const hasUnsavedChanges = Object.keys(dirtySourceMap).length > 0;

  // Focus input when renaming
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  // Handle rename start
  const handleStartRename = () => {
    setRenameValue(notebook?.name || '');
    setIsRenaming(true);
  };

  // Handle rename submit
  const handleRenameSubmit = async () => {
    if (renameValue.trim() && renameValue !== notebook?.name) {
      await renameNotebook(renameValue.trim());
    }
    setIsRenaming(false);
  };

  // Handle rename cancel
  const handleRenameCancel = () => {
    setIsRenaming(false);
  };

  // Handle rename key press
  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      handleRenameCancel();
    }
  };

  // Session status indicator
  const getStatusIcon = () => {
    switch (sessionStatus) {
      case 'ready':
        return <CheckCircle className="w-3 h-3 text-green-500" />;
      case 'busy':
        return <Loader2 className="w-3 h-3 animate-spin text-yellow-500" />;
      case 'connecting':
        return <Loader2 className="w-3 h-3 animate-spin text-blue-500" />;
      case 'error':
        return <XCircle className="w-3 h-3 text-red-500" />;
      default:
        return <Circle className="w-3 h-3 text-muted-foreground" />;
    }
  };

  const getStatusText = () => {
    switch (sessionStatus) {
      case 'ready':
        return 'Kernel Ready';
      case 'busy':
        return 'Running...';
      case 'connecting':
        return 'Connecting...';
      case 'error':
        return 'Kernel Error';
      default:
        return 'Disconnected';
    }
  };

  // Save all dirty cells
  const handleSaveAll = async () => {
    for (const cellId of Object.keys(dirtySourceMap)) {
      await saveCellSource(cellId);
    }
  };

  return (
    <div className="flex items-center gap-2 px-4 py-2 border-b bg-background">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/notebooks')}
        className="mr-2"
      >
        <ChevronLeft className="w-4 h-4 mr-1" />
        Notebooks
      </Button>

      {/* Notebook title - editable */}
      <div className="flex items-center gap-1 flex-1 min-w-0">
        {isRenaming ? (
          <Input
            ref={renameInputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleRenameKeyDown}
            className="h-8 text-lg font-semibold max-w-xs"
          />
        ) : (
          <button
            onClick={handleStartRename}
            className="text-lg font-semibold truncate hover:bg-muted px-2 py-1 rounded transition-colors group flex items-center gap-1"
          >
            {notebook?.name || 'Untitled Notebook'}
            <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
          </button>
        )}
        {hasUnsavedChanges && (
          <span className="text-xs text-muted-foreground">(unsaved)</span>
        )}
      </div>

      <div className="flex items-center gap-1">
        {/* Save button */}
        {hasUnsavedChanges && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSaveAll}
            title="Save all changes"
          >
            <Save className="w-4 h-4" />
          </Button>
        )}

        {/* Add cell */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" title="Add cell">
              <Plus className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => addCell('code')}>
              Code Cell
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => addCell('markdown')}>
              Markdown Cell
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Run current cell */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => selectedCellId && runCell(selectedCellId)}
          disabled={!selectedCellId || executingCellId !== null}
          title="Run selected cell (Shift+Enter)"
        >
          <Play className="w-4 h-4" />
        </Button>

        {/* Run all cells */}
        <Button
          variant="ghost"
          size="sm"
          onClick={runAllCells}
          disabled={executingCellId !== null}
          title="Run all cells"
        >
          <FastForward className="w-4 h-4" />
        </Button>

        {/* Stop execution */}
        <Button
          variant="ghost"
          size="sm"
          onClick={stopKernel}
          disabled={sessionStatus !== 'busy'}
          title="Stop execution"
        >
          <StopCircle className="w-4 h-4" />
        </Button>

        {/* Restart kernel */}
        <Button
          variant="ghost"
          size="sm"
          onClick={restartKernel}
          disabled={sessionStatus === 'disconnected' || sessionStatus === 'connecting'}
          title="Restart kernel"
        >
          <RotateCcw className="w-4 h-4" />
        </Button>

        {/* Kernel status */}
        <div
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs',
            'border bg-muted/50'
          )}
        >
          {getStatusIcon()}
          <span>{getStatusText()}</span>
        </div>

        {/* More options */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleStartRename}>
              Rename Notebook
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={startKernel} disabled={sessionStatus !== 'disconnected'}>
              Start Kernel
            </DropdownMenuItem>
            <DropdownMenuItem onClick={stopKernel} disabled={sessionStatus === 'disconnected'}>
              Stop Kernel
            </DropdownMenuItem>
            <DropdownMenuItem onClick={restartKernel} disabled={sessionStatus === 'disconnected'}>
              Restart Kernel
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={runAllCells} disabled={executingCellId !== null}>
              Run All Cells
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Help toggle */}
        {onToggleHelp && (
          <Button
            variant={showHelp ? 'secondary' : 'ghost'}
            size="sm"
            onClick={onToggleHelp}
            title="Toggle API documentation"
          >
            <HelpCircle className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
